"use client";

import {
  Building2,
  Factory,
  FileSearch,
  Headphones,
  HeartPulse,
  Landmark,
  ShoppingCart,
  Truck,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Density = "home" | "index";

const META: Record<
  string,
  { icon: LucideIcon; label: string; metric: string }
> = {
  manufacturing: { icon: Factory, label: "Line 04", metric: "+3.8% cost" },
  "customer-service": {
    icon: Headphones,
    label: "SLA queue",
    metric: "14 routed",
  },
  insurance: { icon: FileSearch, label: "Claim 4182", metric: "Review" },
  hr: { icon: Users, label: "48 applicants", metric: "6 matches" },
  retail: { icon: ShoppingCart, label: "Store 12", metric: "3 reorders" },
  logistics: { icon: Truck, label: "Load 729", metric: "18m risk" },
  finance: { icon: Landmark, label: "Limit watch", metric: "82% used" },
  healthcare: { icon: HeartPulse, label: "Visit 10:42", metric: "Note ready" },
  energy: { icon: Zap, label: "Grid load", metric: "84% peak" },
  "real-estate": {
    icon: Building2,
    label: "24 assets",
    metric: "91% occupied",
  },
};

function SignalDiagram({ slug }: { slug: string }) {
  switch (slug) {
    case "manufacturing":
      return (
        <div className="iv-diagram iv-manufacturing">
          <span className="iv-tolerance" />
          <div className="iv-conveyor">
            {[0, 1, 2, 3, 4].map((item) => (
              <i
                className={
                  item === 3 ? "iv-motion is-alert iv-variance-item" : ""
                }
                key={item}
              />
            ))}
          </div>
          <span className="iv-tracer iv-motion" />
          <strong className="iv-motion iv-variance-label">variance</strong>
        </div>
      );
    case "customer-service":
      return (
        <div className="iv-diagram iv-support">
          <div className="iv-ticket-stack">
            {[0, 1, 2].map((item) => (
              <i
                className={item === 1 ? "iv-motion is-routing" : ""}
                key={item}
              >
                <span />
              </i>
            ))}
          </div>
          <span className="iv-route-stem" />
          <div className="iv-queues">
            <i />
            <i className="iv-motion is-alert" />
            <i />
          </div>
          <strong>priority</strong>
        </div>
      );
    case "insurance":
      return (
        <div className="iv-diagram iv-insurance">
          <div className="iv-docs">
            <i />
            <i />
            <i />
          </div>
          <span className="iv-scan iv-motion" />
          <div className="iv-rules">
            <i />
            <i className="iv-motion is-alert" />
            <i />
          </div>
          <div className="iv-motion iv-review-gate">
            <span />
            review
          </div>
        </div>
      );
    case "hr":
      return (
        <div className="iv-diagram iv-hr">
          <div className="iv-hr-stage">
            <span>screen</span>
            <span>review</span>
            <span>start</span>
          </div>
          <div className="iv-stage-line">
            <span className="iv-motion iv-stage-progress" />
          </div>
          <div className="iv-stage-nodes">
            <i className="is-complete" />
            <i className="iv-motion is-review" />
            <i />
          </div>
          <strong>human gate</strong>
        </div>
      );
    case "retail":
      return (
        <div className="iv-diagram iv-retail">
          <div className="iv-shelf">
            {Array.from({ length: 15 }, (_, item) => (
              <i className={item === 8 ? "iv-motion is-low" : ""} key={item} />
            ))}
          </div>
          <span className="iv-reorder iv-motion">+24</span>
          <strong>reorder</strong>
        </div>
      );
    case "logistics":
      return (
        <div className="iv-diagram iv-logistics">
          <div className="iv-route-track">
            <i className="iv-route-node n1" />
            <i className="iv-motion iv-route-node n2 is-alert" />
            <i className="iv-route-node n3" />
            <span className="iv-route-tracer iv-motion" />
          </div>
          <strong>eta +18m</strong>
        </div>
      );
    case "finance":
      return (
        <div className="iv-diagram iv-finance">
          <div className="iv-exposure-bars">
            {[42, 58, 69, 88, 76, 94].map((height, item) => (
              <i
                className={item === 5 ? "iv-motion is-breach" : ""}
                style={{ height: `${height}%` }}
                key={item}
              />
            ))}
          </div>
          <span className="iv-limit-line">limit</span>
          <strong className="iv-motion iv-breach-label">breach</strong>
        </div>
      );
    case "healthcare":
      return (
        <div className="iv-diagram iv-healthcare">
          <div className="iv-care-document">
            <span>visit 10:42</span>
            <i />
            <i />
            <i />
          </div>
          <span className="iv-care-route">
            <i className="iv-motion" />
          </span>
          <div className="iv-motion iv-care-note">
            <span>note ready</span>
            <i />
            <i />
          </div>
          <span className="iv-care-review-line">
            <i className="is-complete" />
            <i className="is-complete" />
            <i className="iv-motion is-review" />
          </span>
          <strong className="iv-motion iv-clinician-label">
            clinician review
          </strong>
        </div>
      );
    case "energy":
      return (
        <div className="iv-diagram iv-energy">
          <div className="iv-load-bars">
            {[38, 49, 64, 72, 84, 78].map((height, item) => (
              <i
                className="iv-motion"
                style={{
                  height: `${height}%`,
                  animationDelay: `${item * 70}ms`,
                }}
                key={item}
              />
            ))}
          </div>
          <span className="iv-capacity-line">capacity</span>
          <div className="iv-grid-nodes">
            <i />
            <i className="iv-motion is-alert" />
            <i />
          </div>
        </div>
      );
    case "real-estate":
      return (
        <div className="iv-diagram iv-real-estate">
          <div className="iv-building">
            {Array.from({ length: 16 }, (_, item) => (
              <i
                className={item === 6 || item === 11 ? "is-open" : ""}
                key={item}
              />
            ))}
          </div>
          <div className="iv-renewal">
            <span>renewal</span>
            <strong>18d</strong>
            <i>
              <b className="iv-motion" />
            </i>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export function IndustrySignal({
  slug,
  density = "index",
}: {
  slug: string;
  density?: Density;
}) {
  const meta = META[slug] ?? META.manufacturing;
  const Icon = meta.icon;
  const sceneRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (density !== "home") return;

    const scene = sceneRef.current;
    if (
      !scene ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(Boolean(entry?.isIntersecting)),
      { threshold: 0.2 },
    );
    observer.observe(scene);
    return () => observer.disconnect();
  }, [density]);

  return (
    <div
      ref={sceneRef}
      className={`iv-scene iv-${density} iv-${slug}${
        density === "home" && isVisible ? " is-motion-active" : ""
      }`}
      aria-hidden="true"
    >
      <div className="iv-readout">
        <span>
          <Icon size={13} />
          {meta.label}
        </span>
        <strong>{meta.metric}</strong>
      </div>
      <div className="iv-field">
        <SignalDiagram slug={slug} />
      </div>
      <div className="iv-foot">
        <span>Sanitized signal</span>
        <span>Detect → route → act</span>
      </div>
    </div>
  );
}

export function IndustrySignalStyles() {
  return (
    <style>{`
      .iv-scene { position: relative; contain: layout paint; overflow: hidden; border: 1px solid var(--line,#e4e4e7); border-radius: 13px; background: linear-gradient(145deg,#fff 0%,#fafafa 52%,#f4f4f5 100%); color: #09090b; transition: border-color .28s ease,background .28s ease; }
      .iv-scene::before { content: ""; position: absolute; inset: 0; background-image: linear-gradient(rgba(9,9,11,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(9,9,11,.04) 1px,transparent 1px); background-size: 18px 18px; mask-image: linear-gradient(to bottom,#000,transparent 92%); }
      .iv-readout,.iv-foot { position: relative; z-index: 3; display: flex; align-items: center; justify-content: space-between; gap: 10px; color: #71717a; font-size: 7px; letter-spacing: .1em; text-transform: uppercase; }
      .iv-readout { height: 35px; padding: 0 11px; border-bottom: 1px solid #e4e4e7; background: rgba(255,255,255,.72); }
      .iv-readout span { display: flex; align-items: center; gap: 6px; }
      .iv-readout strong { color: #18181b; font-size: 7px; }
      .iv-foot { position: absolute; left: 11px; right: 11px; bottom: 9px; color: #a1a1aa; font-size: 6px; }
      .iv-field { position: absolute; z-index: 2; left: 0; right: 0; top: 35px; bottom: 24px; }
      .iv-diagram { position: absolute; inset: 0; }
      .iv-diagram > strong { position: absolute; padding: 4px 6px; border-radius: 5px; background: #18181b; color: #fff; font-size: 6px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
      .iv-index { height: 180px; }
      .iv-home { height: 190px; }
      .iv-motion { animation-name: none; animation-duration: 8s; animation-delay: var(--iv-delay,0s); animation-timing-function: var(--iv-ease,linear); animation-iteration-count: infinite; animation-fill-mode: both; }
      .iv-scene.is-motion-active .iv-motion,
      :is(.ux-card,.v4-case):is(:hover,:focus-visible) .iv-scene.iv-index .iv-motion { animation-name: var(--iv-animation); }
      :is(.ux-card,.v4-case):is(:hover,:focus-visible) .iv-scene { border-color: rgba(9,9,11,.3); background: linear-gradient(145deg,#fff,#f4f4f5); }

      .iv-tolerance { position: absolute; left: 12%; right: 11%; top: 34%; border-top: 1px dashed #a1a1aa; }
      .iv-conveyor { position: absolute; left: 12%; right: 11%; bottom: 25%; display: flex; gap: 7px; padding-bottom: 8px; border-bottom: 1px solid #71717a; }
      .iv-conveyor i { flex: 1; height: 29px; display: block; border: 1px solid #a1a1aa; background: #fff; }
      .iv-conveyor i.is-alert { --iv-animation: ivVarianceItem; --iv-ease: cubic-bezier(.45,0,.55,1); background: repeating-linear-gradient(135deg,#18181b 0 3px,#52525b 3px 5px); border-color: #18181b; }
      .iv-tracer { --iv-animation: ivManufacturingTravel; position: absolute; left: 13%; bottom: 20%; width: 7px; height: 7px; border-radius: 50%; background: #18181b; opacity: 0; }
      .iv-manufacturing > strong { right: 10%; top: 19%; }
      .iv-variance-label { --iv-animation: ivAlertLabel; --iv-ease: cubic-bezier(.45,0,.55,1); }

      .iv-ticket-stack { position: absolute; left: 9%; top: 18%; width: 52%; display: grid; gap: 7px; }
      .iv-ticket-stack i { height: 20px; display: block; border: 1px solid #d4d4d8; border-radius: 5px; background: #fff; }
      .iv-ticket-stack i::before { content: ""; display: block; width: 30%; height: 3px; margin: 5px 0 0 6px; background: #a1a1aa; box-shadow: 0 6px 0 #e4e4e7; }
      .iv-ticket-stack i.is-routing { --iv-animation: ivSupportRoute; --iv-ease: cubic-bezier(.2,0,0,1); position: relative; z-index: 2; }
      .iv-route-stem { position: absolute; left: 66%; top: 26%; bottom: 26%; border-left: 1px solid #71717a; }
      .iv-queues { position: absolute; right: 9%; top: 22%; bottom: 22%; display: grid; gap: 7px; }
      .iv-queues i { width: 29px; border: 1px solid #a1a1aa; border-radius: 5px; background: #fff; }
      .iv-queues i.is-alert { --iv-animation: ivPriority; --iv-ease: cubic-bezier(.45,0,.55,1); background: #18181b; border-color: #18181b; }
      .iv-support > strong { right: 9%; bottom: 14%; }

      .iv-docs { position: absolute; left: 10%; top: 18%; width: 41%; height: 64%; }
      .iv-docs i { position: absolute; inset: 0; border: 1px solid #a1a1aa; border-radius: 5px; background: #fff; }
      .iv-docs i:nth-child(2) { transform: translate(7px,5px); }
      .iv-docs i:nth-child(3) { transform: translate(14px,10px); }
      .iv-docs i:last-child::before { content: ""; position: absolute; left: 9px; right: 9px; top: 13px; height: 3px; background: #71717a; box-shadow: 0 9px 0 #d4d4d8,0 18px 0 #d4d4d8; }
      .iv-scan { --iv-animation: ivScan; position: absolute; left: 16%; width: 37%; top: 18%; height: 1px; background: #18181b; box-shadow: 0 0 0 2px rgba(9,9,11,.06); opacity: 0; }
      .iv-rules { position: absolute; left: 62%; top: 22%; display: flex; gap: 7px; }
      .iv-rules i { width: 7px; height: 7px; border: 1px solid #71717a; border-radius: 50%; background: #fff; }
      .iv-rules i.is-alert { --iv-animation: ivRuleAlert; --iv-ease: cubic-bezier(.45,0,.55,1); background: #18181b; }
      .iv-review-gate { --iv-animation: ivReviewGate; --iv-ease: cubic-bezier(.45,0,.55,1); position: absolute; right: 9%; bottom: 20%; width: 70px; height: 42px; display: grid; place-items: center; border: 1px solid #18181b; border-radius: 8px; font-size: 6px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
      .iv-review-gate span { position: absolute; width: 10px; height: 10px; top: -6px; border: 1px solid #18181b; border-radius: 50%; background: #fff; }

      .iv-hr-stage { position: absolute; left: 9%; right: 9%; top: 13%; display: grid; grid-template-columns: repeat(3,1fr); color: #a1a1aa; font-size: 6px; letter-spacing: .08em; text-align: center; text-transform: uppercase; }
      .iv-stage-line { position: absolute; left: 14%; right: 14%; top: 52%; border-top: 1px solid #a1a1aa; }
      .iv-stage-progress { --iv-animation: ivHrProgress; --iv-ease: cubic-bezier(.16,1,.3,1); position: absolute; left: 0; top: -1px; width: 50%; height: 2px; background: #18181b; transform-origin: left center; }
      .iv-stage-nodes { position: absolute; left: 14%; right: 14%; top: 52%; display: flex; justify-content: space-between; }
      .iv-stage-nodes i { width: 15px; height: 15px; margin-top: -7px; border: 1px solid #71717a; border-radius: 50%; background: #fff; }
      .iv-stage-nodes i.is-complete,.iv-stage-nodes i.is-review { background: #18181b; }
      .iv-stage-nodes i.is-review { --iv-animation: ivHrReview; --iv-ease: cubic-bezier(.45,0,.55,1); }
      .iv-hr > strong { right: 9%; bottom: 15%; }

      .iv-shelf { position: absolute; left: 10%; top: 17%; width: 62%; display: grid; grid-template-columns: repeat(5,1fr); gap: 5px; padding: 8px; border: 1px solid #a1a1aa; }
      .iv-shelf i { height: 18px; display: block; border: 1px solid #d4d4d8; background: #fff; }
      .iv-shelf i.is-low { --iv-animation: ivRefill; --iv-ease: cubic-bezier(.16,1,.3,1); align-self: end; background: #18181b; transform: scaleY(.28); transform-origin: center bottom; }
      .iv-reorder { --iv-animation: ivReorder; --iv-ease: cubic-bezier(.45,0,.55,1); position: absolute; right: 10%; top: 30%; width: 42px; height: 42px; display: grid; place-items: center; border: 1px solid #18181b; border-radius: 50%; background: #fff; font-size: 8px; font-weight: 700; }
      .iv-retail > strong { right: 9%; bottom: 15%; }

      .iv-route-track { position: absolute; left: 12%; right: 12%; top: 51%; height: 1px; border-top: 1px solid #71717a; transform: rotate(-7deg); }
      .iv-route-node { position: absolute; top: -7px; width: 13px; height: 13px; border: 1px solid #71717a; border-radius: 50%; background: #fff; }
      .iv-route-node.n1 { left: 0; transform: translateX(-50%); }
      .iv-route-node.n2 { left: 50%; transform: translateX(-50%); }
      .iv-route-node.n3 { right: 0; transform: translateX(50%); }
      .iv-route-node.is-alert { --iv-animation: ivRouteRisk; --iv-ease: cubic-bezier(.45,0,.55,1); background: #18181b; box-shadow: 0 0 0 5px rgba(9,9,11,.07); }
      .iv-route-tracer { --iv-animation: ivLogisticsTravel; position: absolute; left: 0; top: -4px; width: 7px; height: 7px; border-radius: 50%; background: #18181b; opacity: 0; transform: translateX(-50%); }
      .iv-logistics > strong { right: 9%; bottom: 15%; }

      .iv-exposure-bars { position: absolute; left: 11%; right: 11%; bottom: 21%; height: 63%; display: flex; align-items: end; gap: 7px; }
      .iv-exposure-bars i { flex: 1; display: block; border: 1px solid #a1a1aa; border-bottom: 0; background: rgba(255,255,255,.78); transform-origin: center bottom; }
      .iv-exposure-bars i.is-breach { --iv-animation: ivFinanceBreach; --iv-ease: cubic-bezier(.16,1,.3,1); background: #18181b; }
      .iv-limit-line { position: absolute; left: 9%; right: 9%; top: 31%; padding-top: 3px; border-top: 1px dashed #71717a; color: #a1a1aa; font-size: 6px; text-align: right; text-transform: uppercase; }
      .iv-finance > strong { right: 9%; top: 12%; }
      .iv-breach-label { --iv-animation: ivFinanceLabel; --iv-ease: cubic-bezier(.45,0,.55,1); }

      .iv-care-document,.iv-care-note { position: absolute; top: 18%; height: 58%; padding: 8px; border: 1px solid #a1a1aa; border-radius: 6px; background: #fff; }
      .iv-care-document { left: 9%; width: 34%; }
      .iv-care-document span,.iv-care-note span { display: block; font-size: 6px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; }
      .iv-care-document i,.iv-care-note i { display: block; height: 3px; margin-top: 8px; background: #d4d4d8; }
      .iv-care-document i:first-of-type,.iv-care-note i:first-of-type { background: #71717a; }
      .iv-care-route { position: absolute; left: 45%; right: 42%; top: 47%; border-top: 1px solid #a1a1aa; }
      .iv-care-route i { --iv-animation: ivCareRoute; --iv-ease: cubic-bezier(.16,1,.3,1); position: absolute; left: 0; top: -1px; width: 100%; height: 2px; background: #18181b; transform-origin: left center; }
      .iv-care-note { --iv-animation: ivCareNote; --iv-ease: cubic-bezier(.16,1,.3,1); right: 9%; width: 31%; border-color: #18181b; }
      .iv-care-review-line { position: absolute; left: 13%; right: 13%; bottom: 18%; display: flex; justify-content: space-between; border-top: 1px solid #a1a1aa; }
      .iv-care-review-line i { width: 9px; height: 9px; margin-top: -5px; border: 1px solid #71717a; border-radius: 50%; background: #fff; }
      .iv-care-review-line i.is-complete,.iv-care-review-line i.is-review { background: #18181b; }
      .iv-care-review-line i.is-review { --iv-animation: ivCareReview; --iv-ease: cubic-bezier(.45,0,.55,1); }
      .iv-healthcare > strong { right: 9%; bottom: 8%; }
      .iv-clinician-label { --iv-animation: ivCareLabel; --iv-ease: cubic-bezier(.45,0,.55,1); }

      .iv-load-bars { position: absolute; left: 10%; width: 58%; bottom: 20%; height: 65%; display: flex; align-items: end; gap: 6px; }
      .iv-load-bars i { --iv-animation: ivEnergyLoad; --iv-ease: cubic-bezier(.45,0,.55,1); flex: 1; display: block; border: 1px solid #a1a1aa; border-bottom: 0; background: #fff; transform-origin: center bottom; }
      .iv-capacity-line { position: absolute; left: 9%; width: 62%; top: 27%; border-top: 1px dashed #71717a; color: #a1a1aa; font-size: 6px; text-align: right; text-transform: uppercase; }
      .iv-grid-nodes { position: absolute; right: 9%; top: 26%; display: grid; gap: 9px; }
      .iv-grid-nodes i { width: 22px; height: 22px; display: block; border: 1px solid #a1a1aa; border-radius: 6px; background: #fff; }
      .iv-grid-nodes i.is-alert { --iv-animation: ivEnergyRisk; --iv-ease: cubic-bezier(.45,0,.55,1); background: #18181b; }

      .iv-building { position: absolute; left: 11%; top: 14%; width: 44%; display: grid; grid-template-columns: repeat(4,1fr); gap: 5px; padding: 9px; border: 1px solid #71717a; background: #fff; }
      .iv-building i { height: 13px; display: block; background: #d4d4d8; }
      .iv-building i.is-open { background: #18181b; }
      .iv-renewal { position: absolute; right: 10%; top: 24%; width: 31%; padding-left: 9px; border-left: 1px solid #71717a; }
      .iv-renewal span,.iv-renewal strong { display: block; }
      .iv-renewal span { color: #a1a1aa; font-size: 6px; letter-spacing: .08em; text-transform: uppercase; }
      .iv-renewal strong { margin-top: 7px; font-size: 22px; }
      .iv-renewal > i { display: block; width: 100%; height: 4px; margin-top: 9px; overflow: hidden; background: #d4d4d8; }
      .iv-renewal b { --iv-animation: ivRenewal; --iv-ease: cubic-bezier(.16,1,.3,1); display: block; width: 100%; height: 100%; background: #18181b; transform-origin: left center; }

      @keyframes ivManufacturingTravel {
        0%,16% { left: 13%; opacity: 0; }
        20% { left: 13%; opacity: 1; }
        52%,70% { left: 65%; opacity: 1; }
        78% { left: 65%; opacity: 0; }
        82%,100% { left: 13%; opacity: 0; }
      }
      @keyframes ivVarianceItem { 0%,48%,100% { transform: scale(1); } 56%,68% { transform: scale(1.06); } }
      @keyframes ivAlertLabel { 0%,48%,100% { opacity: .62; transform: scale(.96); } 56%,70% { opacity: 1; transform: scale(1); } }
      @keyframes ivSupportRoute {
        0%,24%,100% { opacity: 1; transform: translateX(0) scale(1); }
        46%,54% { opacity: 1; transform: translateX(clamp(48px,18vw,76px)) scale(.96); }
        62% { opacity: 0; transform: translateX(clamp(48px,18vw,76px)) scale(.96); }
        64% { opacity: 0; transform: translateX(0) scale(1); }
        70% { opacity: 1; transform: translateX(0) scale(1); }
      }
      @keyframes ivPriority { 0%,44%,100% { transform: scale(1); } 52% { transform: scale(1.12); } 64% { transform: scale(1); } }
      @keyframes ivScan {
        0%,14%,100% { opacity: 0; transform: translateY(0); }
        18% { opacity: 1; transform: translateY(0); }
        46% { opacity: 1; transform: translateY(68px); }
        52%,96% { opacity: 0; transform: translateY(68px); }
      }
      @keyframes ivRuleAlert { 0%,46%,100% { transform: scale(1); } 54% { transform: scale(1.55); } 62% { transform: scale(1); } }
      @keyframes ivReviewGate { 0%,58%,100% { transform: scale(1); } 66%,76% { transform: scale(1.04); } 84% { transform: scale(1); } }
      @keyframes ivHrProgress {
        0%,16%,100% { opacity: 0; transform: scaleX(0); }
        20% { opacity: 1; transform: scaleX(0); }
        48%,74% { opacity: 1; transform: scaleX(1); }
        82% { opacity: 0; transform: scaleX(1); }
        86% { opacity: 0; transform: scaleX(0); }
      }
      @keyframes ivHrReview { 0%,44%,100% { opacity: .38; transform: scale(1); } 54%,70% { opacity: 1; transform: scale(1.14); } 80% { opacity: .38; transform: scale(1); } }
      @keyframes ivReorder { 0%,38%,100% { transform: scale(1); } 46% { transform: scale(1.1); } 56% { transform: scale(1); } }
      @keyframes ivRefill {
        0%,58%,100% { opacity: 1; transform: scaleY(.28); }
        68%,80% { opacity: 1; transform: scaleY(1); }
        86% { opacity: 0; transform: scaleY(1); }
        90% { opacity: 0; transform: scaleY(.28); }
        94% { opacity: 1; transform: scaleY(.28); }
      }
      @keyframes ivLogisticsTravel {
        0%,14%,100% { left: 0; opacity: 0; }
        18% { left: 0; opacity: 1; }
        36%,54% { left: 50%; opacity: 1; }
        72% { left: 100%; opacity: 1; }
        80% { left: 100%; opacity: 0; }
        86% { left: 0; opacity: 0; }
      }
      @keyframes ivRouteRisk { 0%,32%,100% { transform: translateX(-50%) scale(1); } 40%,54% { transform: translateX(-50%) scale(1.16); } 64% { transform: translateX(-50%) scale(1); } }
      @keyframes ivFinanceBreach { 0%,18%,100% { transform: scaleY(.72); } 48%,76% { transform: scaleY(1); } }
      @keyframes ivFinanceLabel { 0%,44%,100% { opacity: .4; transform: scale(.96); } 52%,78% { opacity: 1; transform: scale(1); } }
      @keyframes ivCareRoute {
        0%,18%,100% { opacity: 0; transform: scaleX(0); }
        22% { opacity: 1; transform: scaleX(0); }
        46%,54% { opacity: 1; transform: scaleX(1); }
        60% { opacity: 0; transform: scaleX(1); }
      }
      @keyframes ivCareNote { 0%,42%,100% { opacity: .42; transform: translateY(3px); } 50%,76% { opacity: 1; transform: translateY(0); } 86% { opacity: .42; transform: translateY(3px); } }
      @keyframes ivCareReview { 0%,56%,100% { opacity: .34; transform: scale(.9); } 64%,80% { opacity: 1; transform: scale(1.18); } 88% { opacity: .34; transform: scale(.9); } }
      @keyframes ivCareLabel { 0%,56%,100% { opacity: .42; transform: scale(.96); } 64%,80% { opacity: 1; transform: scale(1); } }
      @keyframes ivEnergyLoad { 0%,18%,100% { transform: scaleY(.82); } 50%,76% { transform: scaleY(1); } }
      @keyframes ivEnergyRisk { 0%,46%,100% { transform: scale(1); } 56%,72% { transform: scale(1.14); } 82% { transform: scale(1); } }
      @keyframes ivRenewal {
        0%,16%,100% { opacity: 1; transform: scaleX(.25); }
        54%,78% { opacity: 1; transform: scaleX(1); }
        84% { opacity: 0; transform: scaleX(1); }
        90% { opacity: 0; transform: scaleX(.25); }
        94% { opacity: 1; transform: scaleX(.25); }
      }

      @media (max-width: 520px) { .iv-index { height: 156px; } .iv-home { height: 170px; } }
      @media (prefers-reduced-motion: reduce) { .iv-motion { animation: none !important; } .iv-scene { transition: none !important; } }
    `}</style>
  );
}
